// src/routes/payment-routes.tsx
import { lazy } from 'react';
import type { RoutesProps } from './routes';
import {
    FaCreditCard,
    FaFileInvoiceDollar,
    FaMoneyCheckAlt,
    FaReceipt,
    FaHistory,
    FaWallet,
    FaUndo,
} from 'react-icons/fa';
import { MdPayment, MdAccountBalanceWallet } from 'react-icons/md';
import { HiCash } from 'react-icons/hi';
import { BiStats } from 'react-icons/bi';
import {
    FileText,
    Receipt,
    Download,
    AlertTriangle,
    CreditCard as CreditCardIcon,
    Activity,
} from 'lucide-react';

// Existing payment components
const PaymentDashboard = lazy(() => import('@/pages/marketplace/payments/PaymentDashboard'));
const InstallmentPayment = lazy(() => import('@/pages/marketplace/payments/InstallmentPayment'));
const RefundManagement = lazy(() => import('@/pages/marketplace/payments/RefundManagement'));
const CheckoutPageUpdated = lazy(() => import('@/pages/marketplace/CheckoutPage'));
const PaymentReturnPage = lazy(() => import('@/pages/marketplace/payments/PaymentReturnPage'));
const PaymentMockPage = lazy(() => import('@/pages/marketplace/payments/PaymentMockPage'));
const AdminPaymentManagement = lazy(() => import('@/pages/admin/payment/AdminPaymentManagement'));

// ✅ NEW: MVP User Pages
const MyPurchasedLicensesPage = lazy(() => import('@/pages/marketplace/pages/MyPurchasedLicensesPage'));
const LicenseDownloadPage = lazy(() => import('@/pages/marketplace/pages/LicenseDownloadPage'));
const InvoiceListPage = lazy(() => import('@/pages/marketplace/pages/InvoiceListPage'));
const InvoiceDetailPage = lazy(() => import('@/pages/marketplace/pages/InvoiceDetailPage'));
const CreateDisputePage = lazy(() => import('@/pages/marketplace/pages/CreateDisputePage'));

// ✅ NEW: MVP Admin Pages (TailwindCSS)
const PaymentVerificationQueuePage = lazy(() => import('@/pages/marketplace/pages/PaymentVerificationQueuePage'));
const AdminDisputeQueue = lazy(() => import('@/pages/admin/payment/AdminDisputeQueue'));
const LicenseActivationMonitor = lazy(() => import('@/pages/admin/payment/LicenseActivationMonitor'));

// Payment routes
export const paymentRoutes: RoutesProps[] = [
    // ============================================
    // USER PAYMENT ROUTES
    // ============================================

    // User Payment Dashboard
    {
        path: '/dashboard/payments',
        name: 'Bảng điều khiển thanh toán',
        element: <PaymentDashboard />,
        roles: ['right_owner', 'licensee', 'admin', 'staff'],
        icon: FaCreditCard,
    },
    {
        path: '/dashboard/payments/installments',
        name: 'Thanh toán trả góp',
        element: <InstallmentPayment />,
        roles: ['right_owner', 'licensee'],
        icon: MdPayment,
    },
    {
        path: '/dashboard/payments/refunds',
        name: 'Quản lý hoàn tiền',
        element: <RefundManagement />,
        roles: ['right_owner', 'licensee', 'admin', 'staff'],
        icon: FaUndo,
    },

    // ✅ NEW: License Management (MVP)
    {
        path: '/licenses',
        name: 'Licenses của tôi',
        element: <MyPurchasedLicensesPage />,
        roles: ['right_owner', 'licensee'],
        icon: FileText,
    },
    {
        path: '/licenses/:licenseId/download',
        name: 'Tải xuống License',
        element: <LicenseDownloadPage />,
        roles: ['right_owner', 'licensee'],
        icon: Download,
        hideInNav: true,
    },

    // ✅ NEW: Invoice Management (MVP)
    {
        path: '/invoices',
        name: 'Hóa đơn',
        element: <InvoiceListPage />,
        roles: ['right_owner', 'licensee'],
        icon: Receipt,
    },
    {
        path: '/invoices/:invoiceId',
        name: 'Chi tiết hóa đơn',
        element: <InvoiceDetailPage />,
        roles: ['right_owner', 'licensee'],
        icon: FaFileInvoiceDollar,
        hideInNav: true,
    },

    // ✅ NEW: Dispute Management (MVP)
    {
        path: '/disputes/create',
        name: 'Báo cáo vấn đề',
        element: <CreateDisputePage />,
        roles: ['right_owner', 'licensee'],
        icon: AlertTriangle,
    },

    // ============================================
    // MARKETPLACE CHECKOUT ROUTES
    // ============================================

    {
        path: '/marketplace/checkout/:contentId/:licenseId',
        name: 'Thanh toán',
        element: <CheckoutPageUpdated />,
        roles: ['right_owner', 'licensee'],
        icon: FaMoneyCheckAlt,
        hideInNav: true,
    },
    {
        path: '/marketplace/payment-return',
        name: 'Kết quả thanh toán',
        element: <PaymentReturnPage />,
        roles: ['right_owner', 'licensee'],
        icon: FaReceipt,
        hideInNav: true,
    },
    {
        path: '/payment/mock',
        name: 'Kết quả thanh toán',
        element: <PaymentMockPage />,
        roles: ['right_owner', 'licensee'],
        icon: FaReceipt,
        hideInNav: true,
    },

    // ============================================
    // ADMIN PAYMENT ROUTES
    // ============================================

    {
        path: '/admin/payments',
        name: 'Quản lý thanh toán (Admin)',
        element: <AdminPaymentManagement />,
        roles: ['admin', 'staff'],
        icon: FaWallet,
    },

    // ✅ NEW: Payment Verification (MVP)
    {
        path: '/admin/payments/verify',
        name: 'Xác minh Thanh toán',
        element: <PaymentVerificationQueuePage />,
        roles: ['admin', 'staff'],
        icon: CreditCardIcon,
    },

    // ✅ NEW: Dispute Management (MVP)
    {
        path: '/admin/disputes',
        name: 'Quản lý Disputes',
        element: <AdminDisputeQueue />,
        roles: ['admin', 'staff', 'support_specialist'],
        icon: AlertTriangle,
    },

    // ✅ NEW: License Monitoring (MVP)
    {
        path: '/admin/licenses/monitor',
        name: 'Monitor Kích hoạt License',
        element: <LicenseActivationMonitor />,
        roles: ['admin', 'staff', 'support_specialist'],
        icon: Activity,
    },
];

// ✅ UPDATED: Navigation structure for payment module
export const paymentNavigationStructure = {
    // User Payments Section
    userPayments: {
        key: 'user-payments',
        translationKey: 'userPayments',
        icon: FaCreditCard,
        roles: ['right_owner', 'licensee'],
        children: [
            {
                key: 'payment-dashboard',
                translationKey: 'paymentDashboard',
                path: '/dashboard/payments',
                icon: FaCreditCard,
                roles: ['right_owner', 'licensee', 'admin', 'staff'],
            },
            {
                key: 'installment-payments',
                translationKey: 'installmentPayments',
                path: '/dashboard/payments/installments',
                icon: MdPayment,
                roles: ['right_owner', 'licensee'],
            },
            {
                key: 'refund-management',
                translationKey: 'refundManagement',
                path: '/dashboard/payments/refunds',
                icon: FaUndo,
                roles: ['right_owner', 'licensee', 'admin', 'staff'],
            },
        ],
    },

    // ✅ NEW: My Licenses & Invoices Section
    myLicensesAndInvoices: {
        key: 'my-licenses-invoices',
        translationKey: 'myLicensesInvoices',
        icon: FileText,
        roles: ['right_owner', 'licensee'],
        children: [
            {
                key: 'my-licenses',
                translationKey: 'myLicenses',
                path: '/licenses',
                icon: FileText,
                roles: ['right_owner', 'licensee'],
            },
            {
                key: 'my-invoices',
                translationKey: 'myInvoices',
                path: '/invoices',
                icon: Receipt,
                roles: ['right_owner', 'licensee'],
            },
        ],
    },

    // ✅ NEW: Support Section
    support: {
        key: 'support',
        translationKey: 'support',
        icon: AlertTriangle,
        roles: ['right_owner', 'licensee'],
        children: [
            {
                key: 'create-dispute',
                translationKey: 'createDispute',
                path: '/disputes/create',
                icon: AlertTriangle,
                roles: ['right_owner', 'licensee'],
            },
        ],
    },

    // Admin Payments Section
    adminPayments: {
        key: 'admin-payments',
        translationKey: 'adminPayments',
        icon: FaWallet,
        roles: ['admin', 'staff'],
        children: [
            {
                key: 'admin-payment-management',
                translationKey: 'adminPaymentManagement',
                path: '/admin/payments',
                icon: FaWallet,
                roles: ['admin', 'staff'],
            },
            // ✅ NEW: Payment Verification
            {
                key: 'payment-verification',
                translationKey: 'paymentVerification',
                path: '/admin/payments/verify',
                icon: CreditCardIcon,
                roles: ['admin', 'staff'],
                badge: 'unverifiedPaymentCount',
            },
        ],
    },

    // ✅ NEW: Admin Dispute Management
    adminDisputes: {
        key: 'admin-disputes',
        translationKey: 'adminDisputes',
        icon: AlertTriangle,
        roles: ['admin', 'staff', 'support_specialist'],
        children: [
            {
                key: 'dispute-queue',
                translationKey: 'disputeQueue',
                path: '/admin/disputes',
                icon: AlertTriangle,
                roles: ['admin', 'staff', 'support_specialist'],
                badge: 'disputeCount',
            },
        ],
    },

    // ✅ NEW: Admin License Monitoring
    adminLicenses: {
        key: 'admin-licenses',
        translationKey: 'adminLicenses',
        icon: Activity,
        roles: ['admin', 'staff', 'support_specialist'],
        children: [
            {
                key: 'license-monitor',
                translationKey: 'licenseMonitor',
                path: '/admin/licenses/monitor',
                icon: Activity,
                roles: ['admin', 'staff', 'support_specialist'],
                badge: 'failedActivationCount',
            },
        ],
    },
};

// Helper functions
export const getPaymentRoutesForRole = (userRoles: string[]): RoutesProps[] => {
    return paymentRoutes.filter((route) => {
        if (!route.roles || route.roles.length === 0) return true;
        return route.roles.some((role) => userRoles.includes(role));
    });
};

export const hasAccessToPayments = (userRoles: string[]): boolean => {
    const allowedRoles = ['right_owner', 'licensee', 'admin', 'staff'];
    return userRoles.some(role => allowedRoles.includes(role));
};

export const canManagePayments = (userRoles: string[]): boolean => {
    const managementRoles = ['admin', 'staff'];
    return userRoles.some(role => managementRoles.includes(role));
};

export const canProcessRefunds = (userRoles: string[]): boolean => {
    const refundRoles = ['admin', 'staff'];
    return userRoles.some(role => refundRoles.includes(role));
};

// ✅ NEW: Helper for admin features
export const hasAdminAccess = (userRoles: string[]): boolean => {
    const adminRoles = ['admin', 'staff', 'support_specialist'];
    return userRoles.some(role => adminRoles.includes(role));
};

// ✅ UPDATED: Get payment menu groups with badge counts
export const getPaymentMenuGroups = (
    userRoles: string[],
    badgeCounts?: {
        unverifiedPaymentCount?: number;
        disputeCount?: number;
        failedActivationCount?: number;
    }
) => {
    const menuGroups: any = {};

    // User payment features
    if (hasAccessToPayments(userRoles)) {
        menuGroups.userPayments = {
            key: 'payment-management',
            translationKey: 'paymentManagement',
            icon: FaCreditCard,
            children: [
                paymentNavigationStructure.userPayments.children[0], // Dashboard
                paymentNavigationStructure.userPayments.children[1], // Installments
                paymentNavigationStructure.userPayments.children[2], // Refunds
            ].filter(item => item.roles.some(role => userRoles.includes(role)))
        };

        // ✅ NEW: Licenses & Invoices
        menuGroups.myLicensesInvoices = {
            ...paymentNavigationStructure.myLicensesAndInvoices,
            children: paymentNavigationStructure.myLicensesAndInvoices.children.filter(
                item => item.roles.some(role => userRoles.includes(role))
            )
        };

        // ✅ NEW: Support
        menuGroups.support = {
            ...paymentNavigationStructure.support,
            children: paymentNavigationStructure.support.children.filter(
                item => item.roles.some(role => userRoles.includes(role))
            )
        };
    }

    // Admin payment management
    if (canManagePayments(userRoles)) {
        menuGroups.adminPayments = {
            ...paymentNavigationStructure.adminPayments,
            children: paymentNavigationStructure.adminPayments.children
                .filter(item => item.roles.some(role => userRoles.includes(role)))
                .map(item => {
                    // Add badge count for payment verification
                    if (item.key === 'payment-verification' && badgeCounts?.unverifiedPaymentCount) {
                        return { ...item, badge: badgeCounts.unverifiedPaymentCount };
                    }
                    return item;
                })
        };
    }

    // ✅ NEW: Admin dispute management
    if (hasAdminAccess(userRoles)) {
        menuGroups.adminDisputes = {
            ...paymentNavigationStructure.adminDisputes,
            children: paymentNavigationStructure.adminDisputes.children
                .filter(item => item.roles.some(role => userRoles.includes(role)))
                .map(item => {
                    // Add badge count for disputes
                    if (item.key === 'dispute-queue' && badgeCounts?.disputeCount) {
                        return { ...item, badge: badgeCounts.disputeCount };
                    }
                    return item;
                })
        };

        // ✅ NEW: Admin license monitoring
        menuGroups.adminLicenses = {
            ...paymentNavigationStructure.adminLicenses,
            children: paymentNavigationStructure.adminLicenses.children
                .filter(item => item.roles.some(role => userRoles.includes(role)))
                .map(item => {
                    // Add badge count for failed activations
                    if (item.key === 'license-monitor' && badgeCounts?.failedActivationCount) {
                        return { ...item, badge: badgeCounts.failedActivationCount };
                    }
                    return item;
                })
        };
    }

    return menuGroups;
};

// ✅ NEW: Get navigation items (filter out hideInNav)
export const getPaymentNavigationItems = (userRoles: string[]): RoutesProps[] => {
    return paymentRoutes.filter((route) => {
        // Filter by role
        if (route.roles && !route.roles.some((role) => userRoles.includes(role))) {
            return false;
        }
        // Filter out hidden items
        return !route.hideInNav;
    });
};

export default paymentRoutes;
